import {
  AgxRadioGroup,
  AgxSpacer,
  AgxTextInput,
} from '@urbanx/agx.ui-components2'
import { StaffUpdateCommissionInfoForCampaignRequestChangeType } from '@urbanx/agx.campaigns.client'
import { useEffect, useState } from 'react'
import CommissionEvidenceUpload, {
  EvidenceUploadState,
} from './CommissionEvidenceUpload'
import { AustralianAddress } from '@urbanx/agx.agencies.client'

const editCommissionReasonOptions = [
  {
    label: 'Commission reduction',
    value:
      StaffUpdateCommissionInfoForCampaignRequestChangeType.CommissionReduction,
  },
  {
    label: 'Vendor Choice confirmed',
    value:
      StaffUpdateCommissionInfoForCampaignRequestChangeType.VendorChoiceConfirmed,
  },
  {
    label: 'Other',
    value: StaffUpdateCommissionInfoForCampaignRequestChangeType.Other,
  },
]

interface EditCommissionReasonProps {
  defaultChangeDetails?: ChangeDetails
  onChange?: (details: ChangeDetails) => void
  campaignId: string
  campaignAddress?: AustralianAddress
}

const buildDefaultEvidenceUpload = (): EvidenceUploadState => ({
  files: [],
  isUploading: false,
})

export interface ChangeDetails {
  reasonType: StaffUpdateCommissionInfoForCampaignRequestChangeType
  reason?: string
  evidenceUpload?: EvidenceUploadState
}

const EditCommissionReason = ({
  defaultChangeDetails,
  onChange,
  campaignId,
  campaignAddress,
}: EditCommissionReasonProps) => {
  const [selectedReason, setSelectedReason] =
    useState<StaffUpdateCommissionInfoForCampaignRequestChangeType>(
      () =>
        defaultChangeDetails?.reasonType ??
        StaffUpdateCommissionInfoForCampaignRequestChangeType.CommissionReduction
    )

  const [otherReasonText, setOtherReasonText] = useState<string>(
    () => defaultChangeDetails?.reason ?? ''
  )

  const [evidenceUpload, setEvidenceUpload] = useState<EvidenceUploadState>(
    () => defaultChangeDetails?.evidenceUpload ?? buildDefaultEvidenceUpload()
  )

  useEffect(() => {
    if (!onChange) return

    const updatedChangedDetails = {
      reasonType: selectedReason,
      reason:
        selectedReason ===
        StaffUpdateCommissionInfoForCampaignRequestChangeType.Other
          ? otherReasonText
          : undefined,
      evidenceUpload,
    }

    onChange(updatedChangedDetails)
  }, [selectedReason, otherReasonText, evidenceUpload, onChange])

  const requiresEvidence =
    selectedReason ===
      StaffUpdateCommissionInfoForCampaignRequestChangeType.CommissionReduction ||
    selectedReason ===
      StaffUpdateCommissionInfoForCampaignRequestChangeType.VendorChoiceConfirmed
  const isOtherReason =
    selectedReason ===
    StaffUpdateCommissionInfoForCampaignRequestChangeType.Other

  return (
    <AgxSpacer direction="vertical" fill gap={'spacing-lg'}>
      <AgxRadioGroup
        id="editCommissionReasonRadioGroup"
        layout="vertical"
        options={editCommissionReasonOptions}
        value={selectedReason}
        onChange={(value) => {
          if (value === selectedReason) return

          setSelectedReason(
            value as StaffUpdateCommissionInfoForCampaignRequestChangeType
          )
          setEvidenceUpload(buildDefaultEvidenceUpload())
        }}
        label="Reason *"
        subContent={
          (requiresEvidence || isOtherReason) && (
            <AgxSpacer
              direction="vertical"
              padding={{
                top: 'spacing-2xl',
                bottom: 'spacing-2xl',
                left: 'spacing-lg',
                right: 'spacing-lg',
              }}
              fill
              gap="spacing-lg"
            >
              {isOtherReason && (
                <AgxTextInput
                  id={'editCommissionReasonOtherInput'}
                  type={'text'}
                  defaultValue={otherReasonText}
                  onChange={(value) => {
                    if (value === otherReasonText) return
                    return setOtherReasonText(value)
                  }}
                  required
                  hideOptionalText
                  stretch
                  label="Reason"
                />
              )}
              <CommissionEvidenceUpload
                campaignId={campaignId}
                campaignAddress={campaignAddress}
                reasonType={selectedReason}
                uploadState={evidenceUpload}
                setUploadState={setEvidenceUpload}
                required={requiresEvidence}
              />
            </AgxSpacer>
          )
        }
      ></AgxRadioGroup>
    </AgxSpacer>
  )
}

export default EditCommissionReason
